import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSessionStore } from '@/deploy/stores/session.store';
import { Icon } from '@/deploy/components/ui/Icon';
import { Badge } from '@/deploy/components/ui/Badge';
import { Button } from '@/deploy/components/ui/Button';
import { PageHeader } from '@/deploy/components/layout/PageHeader';
import { auditService, type AuditLog } from '@/deploy/services/audit.service';
import { formatDateTime, formatRelative } from '@/deploy/utils/format';
import styles from './History.module.scss';

// ============================================================
// Page /deploiement/history — Historique des actions (audit log)
// ============================================================
// La liste répond à « qui a lancé quoi, et quand » sur TOUT ce qui mute : releases, déploiements de
// packs et d'APIs, promotions, containers, procédures… Le détail par action (frise des étapes d'une
// release, log d'une promotion) vit sur les pages dédiées ; ici on garde la trace, qui est conservée
// bien plus longtemps que les jobs en mémoire (rétention Mongo côté API).
// ============================================================

const PAGE_SIZE = 50;

/** Catégories d'audit qu'on met en avant dans le filtre (les autres restent visibles via « Toutes »). */
const CATEGORIES: Array<{ value: string; label: string }> = [
  { value: '', label: 'Toutes' },
  { value: 'release', label: 'Releases' },
  { value: 'promotion', label: 'Promotions' },
  { value: 'pack', label: 'Packs' },
  { value: 'container', label: 'Containers' },
  { value: 'cdn', label: 'CDN' },
  { value: 'procedure', label: 'Procédures' },
  { value: 'project', label: 'Projets' },
  { value: 'user', label: 'Utilisateurs' },
  { value: 'auth', label: 'Connexions' },
];

const CATEGORY_ICON: Record<string, Parameters<typeof Icon>[0]['name']> = {
  release: 'git-commit',
  promotion: 'rocket',
  pack: 'package',
  container: 'layers',
  cdn: 'zap',
  procedure: 'check',
  project: 'server',
  user: 'server',
  auth: 'server',
};

function iconFor(category: string): Parameters<typeof Icon>[0]['name'] {
  return CATEGORY_ICON[category] ?? 'git-commit';
}

export function History() {
  const project = useSessionStore((s) => s.currentProject());

  const [entries, setEntries] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const load = useCallback(async (p: number, append: boolean) => {
    if (!project.id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await auditService.list({
        projectId: project.id,
        category: category || undefined,
        search: appliedSearch || undefined,
        page: p,
        pageSize: PAGE_SIZE,
      });
      setEntries((prev) => (append ? [...prev, ...res.items] : res.items));
      setTotal(res.total);
      setPage(p);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement de l\'historique');
    } finally {
      setLoading(false);
    }
  }, [project.id, category, appliedSearch]);

  // Rechargement à chaque changement de projet / filtre / recherche appliquée.
  useEffect(() => { setSelectedId(null); void load(0, false); }, [load]);

  const selected = useMemo(
    () => entries.find((e) => e.id === selectedId) ?? null,
    [entries, selectedId],
  );

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedSearch(search.trim());
  };

  return (
    <div className={styles.page}>
      <PageHeader
        eyebrow="Historique"
        title="Qui a fait quoi"
        subtitle={`Trace des actions sur ${project.name} — releases, déploiements, promotions. ${total} entrée${total > 1 ? 's' : ''}.`}
      />

      <div className={styles.filters}>
        <div className={styles.filterGroup}>
          <span className={styles.filterLabel}>Catégorie</span>
          <div className={styles.filterOptions}>
            {CATEGORIES.map((c) => (
              <button
                key={c.value || 'all'}
                type="button"
                className={[styles.filterOption, category === c.value && styles.filterActive].filter(Boolean).join(' ')}
                onClick={() => setCategory(c.value)}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
        <form className={styles.filterGroup} onSubmit={submitSearch}>
          <span className={styles.filterLabel}>Recherche (cible, action, auteur)</span>
          <div className={styles.filterOptions}>
            <input
              className={styles.searchInput}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="ex. world, promote, lait_frais"
            />
            <Button type="submit" variant="ghost" size="sm" leftIcon={<Icon name="refresh" size={12} />}>
              Filtrer
            </Button>
          </div>
        </form>
      </div>

      {error && <div className={styles.noteFailed}><Icon name="warn" size={14} /> {error}</div>}

      <div className={styles.split}>
        <aside className={styles.list}>
          {entries.length === 0 ? (
            <div className={styles.empty}>
              {loading ? 'Chargement…' : 'Aucune action enregistrée pour ces filtres.'}
            </div>
          ) : (
            <>
              {entries.map((e) => (
                <button
                  key={e.id}
                  className={[styles.listItem, selectedId === e.id && styles.listActive].filter(Boolean).join(' ')}
                  onClick={() => setSelectedId(e.id)}
                >
                  <div className={styles.listHeader}>
                    <span className={[styles.statusDot, e.status === 'ok' ? styles.status_success : styles.status_failed].join(' ')} />
                    <span className={styles.listTitle}>
                      <Icon name={iconFor(e.category)} size={13} /> {e.action}
                    </span>
                  </div>
                  {e.targetLabel && <div className={styles.itemSub}>{e.targetLabel}</div>}
                  <div className={styles.listMeta}>
                    <span>{e.userName}</span>
                    <span className={styles.bullet}>·</span>
                    <span title={formatDateTime(e.timestamp)}>{formatRelative(e.timestamp)}</span>
                  </div>
                </button>
              ))}
              {entries.length < total && (
                <Button variant="ghost" size="sm" loading={loading} onClick={() => void load(page + 1, true)}>
                  Charger plus ({entries.length} / {total})
                </Button>
              )}
            </>
          )}
        </aside>

        <section className={styles.detail}>
          {!selected ? (
            <div className={styles.empty}>Sélectionne une entrée pour voir son détail.</div>
          ) : (
            <div className={styles.detailCard}>
              <header className={styles.detailHeader}>
                <div className={styles.detailTitleBlock}>
                  <div className={styles.listTitle}>
                    <Icon name={iconFor(selected.category)} size={15} /> {selected.action}
                  </div>
                  {selected.targetLabel && <div className={styles.itemSub}>{selected.targetLabel}</div>}
                </div>
                <div className={styles.detailRight}>
                  <Badge tone={selected.status === 'ok' ? 'success' : 'danger'}>
                    {selected.status === 'ok' ? 'OK' : selected.status}
                  </Badge>
                </div>
              </header>

              <div className={styles.detailSection}>
                <h3><Icon name="server" size={14} /> Qui, quand, quoi</h3>
                <div className={styles.itemsList}>
                  <div className={styles.itemRow}>
                    <div className={styles.itemInfo}>
                      <div className={styles.itemLabel}>{selected.userName}</div>
                      <div className={styles.itemSub}>{selected.userEmail ?? selected.userId}</div>
                    </div>
                  </div>
                  <div className={styles.itemRow}>
                    <div className={styles.itemInfo}>
                      <div className={styles.itemLabel}>{formatDateTime(selected.timestamp)}</div>
                      <div className={styles.itemSub}>{formatRelative(selected.timestamp)}</div>
                    </div>
                  </div>
                  <div className={styles.itemRow}>
                    <div className={styles.itemInfo}>
                      <div className={styles.itemLabel}>{selected.category} · {selected.action}</div>
                      {selected.targetId && <div className={styles.itemSub}>cible : {selected.targetId}</div>}
                    </div>
                  </div>
                </div>
              </div>

              {selected.details && Object.keys(selected.details).length > 0 && (
                <div className={styles.detailSection}>
                  <h3><Icon name="git-commit" size={14} /> Détails</h3>
                  <div className={styles.itemsList}>
                    {Object.entries(selected.details).map(([k, v]) => (
                      <div key={k} className={styles.itemRow}>
                        <div className={styles.itemInfo}>
                          <div className={styles.itemLabel}>{k}</div>
                          <div className={styles.itemSub}>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
